import React, { useEffect, useState } from 'react';
import axios from 'axios';
import OpsDetailTabs from './OpsDetailTabs';
import OpsSubscriptionSettings from './OpsSubscriptionSettings';
import useOpsUserDetails from './useOpsUserDetails';
import { getBackendURL } from '../../utils/backendURL';

const PAGE_SIZE = 25;
const accessLabels = { active: 'Active', inactive: 'Account inactive', suspended_plan_limit: 'Suspended by seat limit' };

function Pager({ offset, total, onChange, label }) {
  if (total <= PAGE_SIZE) return null;
  return <div className="flex items-center justify-between mt-3 text-sm">
    <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
    <div className="flex gap-3">
      <button disabled={!offset} onClick={() => onChange(offset - PAGE_SIZE)}
        aria-label={`Previous ${label} page`} className="text-blue-700 disabled:text-gray-400">Previous</button>
      <button disabled={offset + PAGE_SIZE >= total} onClick={() => onChange(offset + PAGE_SIZE)}
        aria-label={`Next ${label} page`} className="text-blue-700 disabled:text-gray-400">Next</button>
    </div>
  </div>;
}

export default function OpsOrganizationPanel({ user, tab, onTabChange, onOpenUser, plans, onRefresh }) {
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [invitationOffset, setInvitationOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    axios.get(`${getBackendURL()}/api/ops/users/${encodeURIComponent(user.id)}/organization`, {
      params: { skip: offset, limit: PAGE_SIZE, invitation_skip: invitationOffset },
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      signal: controller.signal,
    }).then(response => { if (!controller.signal.aborted) setData(response.data); })
      .catch(failure => {
        if (!controller.signal.aborted) setError(failure.response?.status === 404
          ? 'The user or organization could not be found.' : 'Could not load this organization. Please retry.');
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [user.id, offset, invitationOffset, attempt]);

  const tabs = [
    { id: 'information', label: 'Information' }, { id: 'subscription', label: 'Subscription' },
    { id: 'team', label: 'Team' }, { id: 'workspaces', label: 'Workspaces' },
  ];
  return <div className="space-y-5">
    <header>
      <h2 className="text-2xl font-bold text-gray-900">Organization Details</h2>
      <p className="text-gray-600">{data?.organization?.name || user.organization_name || user.email}</p>
    </header>
    {loading && !data ? <p role="status">Loading organization…</p> : error ? (
      <div role="alert" className="admin-card p-6 text-red-700"><p>{error}</p>
        <button onClick={() => setAttempt(value => value + 1)} className="mt-2 underline">Retry</button>
      </div>
    ) : !data?.organization ? <p>This user does not belong to an organization.</p> : (
      <div className="admin-card p-6">
        <OpsDetailTabs value={tab} onChange={onTabChange} tabs={tabs}>{section => <>
          {section === 'information' && <div className="space-y-4">
            <h3 className="font-semibold">{data.organization.name || 'Unnamed organization'}</h3>
            <p>Owner: <button onClick={() => onOpenUser(data.organization.owner_id)} className="text-blue-700 hover:underline">
              {data.organization.owner_email || 'Owner account unavailable'}</button></p>
            <p className="text-sm break-all">Organization ID: {data.organization.id}</p>
            <p>{data.active_members} active / {data.team_limit === -1 ? 'Unlimited' : data.team_limit} seats · {data.members_total} registered users</p>
          </div>}
          {section === 'subscription' && <OpsOwnerSubscription id={data.organization.owner_id} plans={plans}
            onRefresh={async () => { await onRefresh(); setAttempt(value => value + 1); }} />}
          {section === 'workspaces' && <div>
            <h3 className="font-semibold mb-3">Workspaces ({data.workspaces.length})</h3>
            {data.workspaces.length ? <ul className="space-y-3">{data.workspaces.map(workspace => <li key={workspace.id} className="rounded border p-3">
              <p className="font-medium">{workspace.name}</p>
              {workspace.subdomain && <p className="text-gray-600">{workspace.subdomain}.lexa.run</p>}
            </li>)}</ul> : <p className="text-gray-500">No workspaces</p>}
          </div>}
          {section === 'team' && <div className="space-y-6">
            <p className="text-sm">{data.active_members} active / {data.team_limit === -1 ? 'Unlimited' : data.team_limit} seats</p>
        <section>
          <h3 className="font-semibold mb-2">Team Members ({data.members_total})</h3>
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 sticky top-0"><tr>
                <th className="p-2">User</th><th className="p-2">Role</th><th className="p-2">Organization access</th>
              </tr></thead>
              <tbody>{data.members.map(member => <tr key={member.id} className="border-b">
                <td className="p-2"><button onClick={() => onOpenUser(member.id)}
                  aria-label={`View user ${member.email}`} className="text-left text-blue-700 hover:underline">
                  <span className="block font-medium">{member.name || member.email}</span>
                  <span>{member.email}</span>
                </button></td>
                <td className="p-2">{member.is_owner ? 'Owner' : member.role === 'admin' ? 'Team Admin' : 'Team Member'}</td>
                <td className="p-2">{accessLabels[member.access_status] || member.access_status}</td>
              </tr>)}</tbody>
            </table>
          </div>
          <Pager offset={offset} total={data.members_total} onChange={setOffset} label="team members" />
          <p className="text-xs text-gray-500 mt-2">Account inactivity and suspension by seat limit are different states. Opening this view does not change either.</p>
        </section>
        <section>
          <h3 className="font-semibold mb-2">Invitations ({data.invitations_total})</h3>
          {data.invitations.length ? <div className="overflow-x-auto"><table className="w-full text-sm text-left">
            <thead className="bg-gray-50"><tr><th className="p-2">Email</th><th className="p-2">Role</th><th className="p-2">Status</th></tr></thead>
            <tbody>{data.invitations.map(invitation => <tr key={invitation.id} className="border-b">
              <td className="p-2">{invitation.email}</td>
              <td className="p-2">{invitation.role === 'admin' ? 'Team Admin' : 'Team Member'}</td>
              <td className="p-2 capitalize">{invitation.status}</td>
            </tr>)}</tbody>
          </table></div> : <p className="text-sm text-gray-500">No invitations</p>}
          <Pager offset={invitationOffset} total={data.invitations_total} onChange={setInvitationOffset} label="invitations" />
        </section>
          </div>}
        </>}</OpsDetailTabs>
      </div>
    )}
  </div>;
}

function OpsOwnerSubscription({ id, plans, onRefresh }) {
  const { user, error, retry } = useOpsUserDetails(id);
  if (error) return <div role="alert">{error} <button onClick={retry} className="underline">Retry</button></div>;
  if (!user) return <p role="status">Loading subscription…</p>;
  return <OpsSubscriptionSettings user={user} plans={plans} onRefresh={onRefresh} />;
}
